import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useSnackbar } from 'notistack';
import { TSession } from '../Sessions/Sessions';
import { RunMetadata, isRunFinished } from './useRunPipelineMetadata';
import Progress from '../Util/Progress';
import runsService from '../../services/runs';
import {
  Mono,
  StatusPill,
  SummaryActions as Actions,
  SummaryDetails as Details,
  SummaryFacts as Facts,
  SummaryTitle as Title,
  SummaryTitleRow as TitleRow,
  SummaryTop as Top,
  SummaryWrapper as Wrapper,
  SessionLabel as Label,
  formatElapsed,
  formatTime,
} from './RunStatusUi';

interface Props {
  run: { id: string };
  session: TSession | undefined | null;
  runMetadata: RunMetadata | null;
}

const RunSummaryOrchestrator: React.FunctionComponent<Props> = ({ run, session, runMetadata }) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [stopping, setStopping] = useState(false);
  const [, setTick] = useState(0);
  const finished = isRunFinished(runMetadata);

  useEffect(() => {
    if (finished) return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [finished]);

  const status = (runMetadata?.status || 'PENDING').toUpperCase();
  const canVisualize = session?.lastRun?.id === run.id;

  const onStop = async () => {
    try {
      setStopping(true);
      await runsService.stopRun(run.id);
    } catch (e) {
      enqueueSnackbar('Failed to stop the run', { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setStopping(false);
    }
  };

  return (
    <Wrapper>
      <Top>
        <Title>
          <TitleRow>
            <h2>
              {session?.homeTeam?.name} – {session?.awayTeam?.name}
            </h2>
            {session?.label && <Label>{session.label}</Label>}
            <StatusPill status={status} />
          </TitleRow>
          <Facts>
            <span>Session {session?.id}</span>
            {session?.pitch?.name && <span>{session.pitch.name}</span>}
          </Facts>
        </Title>
        <Actions>
          {canVisualize && (
            <button
              type="button"
              className="sciui-button sciui-button--secondary"
              onClick={() => history.push('/session/visualize/' + session?.id + '/')}
            >
              Visualize
            </button>
          )}
          {!finished && (
            <button type="button" className="sciui-button sciui-button--primary" onClick={onStop} disabled={stopping}>
              {stopping ? <Progress /> : 'Stop run'}
            </button>
          )}
        </Actions>
      </Top>
      <Details>
        <div>
          <dt>Started</dt>
          <dd>{formatTime(runMetadata?.startTime)}</dd>
        </div>
        <div>
          <dt>{finished ? 'Duration' : 'Elapsed'}</dt>
          <dd>{formatElapsed(runMetadata?.startTime, runMetadata?.endTime)}</dd>
        </div>
        <div>
          <dt>Finished</dt>
          <dd>{formatTime(runMetadata?.endTime)}</dd>
        </div>
        <div>
          <dt>Run</dt>
          <dd>
            <Mono>{run.id}</Mono>
          </dd>
        </div>
        <div>
          <dt>Orchestrator run</dt>
          <dd>
            {runMetadata?.pipelineRunId ? (
              runMetadata.runPageUrl ? (
                <a href={runMetadata.runPageUrl} target="_blank" rel="noopener noreferrer">
                  <Mono>{runMetadata.pipelineRunId}</Mono>
                </a>
              ) : (
                <Mono>{runMetadata.pipelineRunId}</Mono>
              )
            ) : (
              '—'
            )}
          </dd>
        </div>
      </Details>
    </Wrapper>
  );
};

export default RunSummaryOrchestrator;
